import React, { useState, useEffect } from 'react';
import { ItemsTableDefault } from '../../../../Various/ItemsTable/ItemsTable';
import { formatDateInTimezone, formatDateSimple } from '../../utils/dateUtils';
import { iterateCampaigns } from '../../utils/campaignHelpers';

const CampaignsOverview = ({ 
    clientCampaigns, 
    convertTimestampsToDates, 
    onTableRowClick,
    tableValues,
    setTableValues,
    invoicingFilter,
    setInvoicingFilter,
    columnsToShow = 'all', // 'overview', 'send_invoices', 'send_reports', 'all'
    tableRerenderKey = 0 // Add this prop to manually trigger rerenders
}) => {
    const [sortColumns, setSortColumns] = useState([{ columnKey: 'client_id', direction: 'ASC' }]);

    // Log changes to table values
    useEffect(() => {
        console.log('Table values changed:', tableValues);
    }, [tableValues]);



    // Custom click handler that only fires for Campaign Name column
    const handleTableRowClick = (row, columnKey, index, rowOrig, columnIndex) => {
        if (columnKey === 'campaign_name' && onTableRowClick) {
            onTableRowClick(row, columnKey, index, rowOrig, columnIndex);
        }
    };

    const getAllCampaignsTableData = () => {
        if (!clientCampaigns) {
            return [];
        }
        
        const tableData = [];
        
        iterateCampaigns(clientCampaigns, convertTimestampsToDates, (campaign, campaignId, clientId) => {
            // Apply invoicing filter
            const isInvoicingActive = campaign.enable_invoicing;
            if (invoicingFilter === 'active' && !isInvoicingActive) return;
            if (invoicingFilter === 'not_active' && isInvoicingActive) return;
            
            // Get last report data (only if needed)
            const lastReport = columnsToShow === 'send_reports' && campaign.report_history && campaign.report_history.length > 0 
                ? campaign.report_history.sort((a, b) => new Date(a.to_date) - new Date(b.to_date))[campaign.report_history.length - 1]
                : null;
            
            // Get last invoice data (only if needed)
            const lastInvoice = columnsToShow === 'send_invoices' && campaign.invoicing_history && campaign.invoicing_history.length > 0 
                ? campaign.invoicing_history.sort((a, b) => new Date(a.to_date) - new Date(b.to_date))[campaign.invoicing_history.length - 1]
                : null;
            
            const baseData = {
                client_id: clientId,
                campaign_id: campaignId,
                campaign_name: campaign.campaign_name,
                campaign_start_date: formatDateSimple(campaign.campaign_start_date),
                campaign_end_date: formatDateSimple(campaign.campaign_end_date),
                budget_amount: campaign.campaign_budget?.amount || 'Not set',
                budget_periodicity: campaign.campaign_budget && campaign.campaign_budget.periodicity && campaign.campaign_budget.periodicity_unit ? `${campaign.campaign_budget.periodicity} ${campaign.campaign_budget.periodicity_unit}` : 'Not set',
                budget_allocation_strategy: campaign.campaign_budget?.allocation_strategy || 'Not set',
                display_groups: campaign.campaign_display_groups?.length || 0,
            };

            // Only add report-related fields if they're needed for the current view
            if (columnsToShow === 'send_reports') {
                baseData.count_recipients_report = campaign.bexio_recipients?.filter(recipient => recipient.send_report).length || 0;
                baseData.last_report_date = lastReport ? (lastReport).to_date : 'No reports';
            }

            // Only add invoice-related fields if they're needed for the current view
            if (columnsToShow === 'send_invoices') {
                baseData.count_recipients_invoice = campaign.bexio_recipients?.filter(recipient => recipient.send_invoice).length || 0;
                baseData.last_invoice_date = lastInvoice ? (lastInvoice).to_date : 'No invoices';
                baseData.enable_invoicing = campaign.enable_invoicing ? 'Yes' : 'No';
                baseData.open_billing_amount = campaign.open_billing_data ? `${campaign.open_billing_data.total_amount}` : 'Campaign misconfigured';
                baseData.open_billing_period = campaign.open_billing_data ? `${campaign.open_billing_data.start_date} - ${campaign.open_billing_data.end_date}` : 'Campaign misconfigured';
            }

            tableData.push(baseData);
        });
        
        return tableData;
    };

    // Date picker renderer for from date (Send Invoices)
    const SendInvoicesFromDateRenderer = ({ row }) => {
        const campaignId = row.campaign_id;
        const defaultValue = tableValues.sendInvoicesFromDate?.[campaignId] || '';
        
        return (
            <input
                type="date"
                defaultValue={defaultValue}
                onChange={(e) => {
                    const newValue = e.target.value;
                    console.log('Send Invoices from date onChange triggered:', { campaignId, value: newValue });
                    setTableValues(prev => ({
                        ...prev,
                        sendInvoicesFromDate: {
                            ...prev.sendInvoicesFromDate,
                            [campaignId]: newValue
                        }
                    }));
                }}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%', border: 'none', background: 'transparent' }}
            />
        );
    };

    // Date picker renderer for to date (Send Invoices)
    const SendInvoicesToDateRenderer = ({ row }) => {
        const campaignId = row.campaign_id;
        const defaultValue = tableValues.sendInvoicesToDate?.[campaignId] || '';
        
        return (
            <input
                type="date"
                defaultValue={defaultValue}
                onChange={(e) => {
                    const newValue = e.target.value;
                    console.log('Send Invoices to date onChange triggered:', { campaignId, value: newValue });
                    setTableValues(prev => ({
                        ...prev,
                        sendInvoicesToDate: {
                            ...prev.sendInvoicesToDate,
                            [campaignId]: newValue
                        }
                    }));
                }}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%', border: 'none', background: 'transparent' }}
            />
        );
    };

    // Date picker renderer for from date (Send Reports)
    const SendReportsFromDateRenderer = ({ row }) => {
        const campaignId = row.campaign_id;
        const defaultValue = tableValues.sendReportsFromDate?.[campaignId] || '';
        
        return (
            <input
                type="date"
                defaultValue={defaultValue}
                onChange={(e) => {
                    const newValue = e.target.value;
                    console.log('Send Reports from date onChange triggered:', { campaignId, value: newValue });
                    setTableValues(prev => ({
                        ...prev,
                        sendReportsFromDate: {
                            ...prev.sendReportsFromDate,
                            [campaignId]: newValue
                        }
                    }));
                }}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%', border: 'none', background: 'transparent' }}
            />
        );
    };

    // Date picker renderer for to date (Send Reports)
    const SendReportsToDateRenderer = ({ row }) => {
        const campaignId = row.campaign_id;
        const defaultValue = tableValues.sendReportsToDate?.[campaignId] || '';
        
        return (
            <input
                type="date"
                defaultValue={defaultValue}
                onChange={(e) => {
                    const newValue = e.target.value;
                    console.log('Send Reports to date onChange triggered:', { campaignId, value: newValue });
                    setTableValues(prev => ({
                        ...prev,
                        sendReportsToDate: {
                            ...prev.sendReportsToDate,
                            [campaignId]: newValue
                        }
                    }));
                }}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%', border: 'none', background: 'transparent' }}
            />
        );
    };

    // Action radio buttons renderer for Send Invoices tab
    const SendInvoicesActionRenderer = ({ row }) => {
        const campaignId = row.campaign_id;
        const currentValue = tableValues.sendInvoicesAction?.[campaignId] || 'no_action';
        
        return (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                    <input
                        type="checkbox"
                        defaultChecked={currentValue === 'send_report_and_invoice'}
                        onChange={(e) => {
                            console.log('Send Invoices checkbox onChange triggered:', { campaignId, checked: e.target.checked });
                            setTableValues(prev => ({
                                ...prev,
                                sendInvoicesAction: {
                                    ...prev.sendInvoicesAction,
                                    [campaignId]: e.target.checked ? 'send_report_and_invoice' : 'no_action'
                                }
                            }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                    Send Report and Invoices
                </label>
            </div>
        );
    };

    // Action radio buttons renderer for Send Reports tab
    const SendReportsActionRenderer = ({ row }) => {
        const campaignId = row.campaign_id;
        const currentValue = tableValues.sendReportsAction?.[campaignId] || 'no_action';
        
        return (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                    <input
                        type="checkbox"
                        defaultChecked={currentValue === 'send_report'}
                        onChange={(e) => {
                            console.log('Send Reports checkbox onChange triggered:', { campaignId, checked: e.target.checked });
                            setTableValues(prev => ({
                                ...prev,
                                sendReportsAction: {
                                    ...prev.sendReportsAction,
                                    [campaignId]: e.target.checked ? 'send_report' : 'no_action'
                                }
                            }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                    Send Report
                </label>
            </div>
        );
    };

    // Define column configurations for different views
    const getColumnSettings = () => {
        switch (columnsToShow) {
            case 'overview':
                return {
                    client_id: { name: 'Client' },
                    campaign_name: { name: 'Campaign Name' },
                    campaign_start_date: { name: 'Start Date' },
                    campaign_end_date: { name: 'End Date' },
                    budget_amount: { name: 'Budget Amount' },
                    budget_periodicity: { name: 'Budget Period' },
                    budget_allocation_strategy: { name: 'Budget Allocation Strategy' },
                    display_groups: { name: 'Display Groups' }
                };
            case 'send_invoices':
                return {
                    client_id: { name: 'Client' },
                    campaign_name: { name: 'Campaign Name' },
                    action: { 
                        name: 'Send Report and Invoices',
                        formatter: SendInvoicesActionRenderer,
                        width: 200
                    },
                    open_billing_amount: { name: 'Billing Amount' },
                    from_date: { 
                        name: 'From Date',
                        formatter: SendInvoicesFromDateRenderer,
                        width: 120
                    },
                    to_date: { 
                        name: 'To Date',
                        formatter: SendInvoicesToDateRenderer,
                        width: 120
                    },
                    campaign_start_date: { name: 'Start Date' },
                    campaign_end_date: { name: 'End Date' },
                    budget_amount: { name: 'Budget Amount' },
                    budget_periodicity: { name: 'Budget Period' },
                    count_recipients_invoice: { name: 'Invoice-Recipients' },
                    display_groups: { name: 'Display Groups' },
                    last_invoice_date: { name: 'Last Invoice' },
                    open_billing_period: { name: 'Billing Period' },
                    open_billing_periods: { name: 'Number of Periods' },
                    enable_invoicing: { name: 'Invoicing Active' },
                };
            case 'send_reports':
                return {
                    action: { 
                        name: 'Send Report',
                        formatter: SendReportsActionRenderer,
                        width: 150
                    },
                    from_date: { 
                        name: 'From Date',
                        formatter: SendReportsFromDateRenderer,
                        width: 120
                    },
                    to_date: { 
                        name: 'To Date',
                        formatter: SendReportsToDateRenderer,
                        width: 120
                    },
                    last_report_date: { name: 'Last Report' },
                    count_recipients_report: { name: 'Report-Recipients' },
                    client_id: { name: 'Client' },
                    campaign_name: { name: 'Campaign Name' },
                    campaign_start_date: { name: 'Start Date' },
                    campaign_end_date: { name: 'End Date' },
                    budget_amount: { name: 'Budget Amount' },
                    budget_periodicity: { name: 'Budget Period' },
                    display_groups: { name: 'Display Groups' },
                };
            default:
                return {};
        }
    };

    return (
        <div className='row'>
            <div className='col-12'>
                {/* Filter Section */}
                <div style={{ marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label style={{ fontWeight: 'bold', fontSize: '12px', margin: 0 }}>
                            Filter by Invoicing Status:
                        </label>
                        <div style={{ display: 'flex', gap: '15px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', margin: 0 }}>
                                <input
                                    type="radio"
                                    name="invoicingFilter"
                                    value="both"
                                    checked={invoicingFilter === 'both'}
                                    onChange={(e) => setInvoicingFilter(e.target.value)}
                                />
                                All Campaigns
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', margin: 0 }}>
                                <input
                                    type="radio"
                                    name="invoicingFilter"
                                    value="active"
                                    checked={invoicingFilter === 'active'}
                                    onChange={(e) => setInvoicingFilter(e.target.value)}
                                />
                                Invoicing Active
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', margin: 0 }}>
                                <input
                                    type="radio"
                                    name="invoicingFilter"
                                    value="not_active"
                                    checked={invoicingFilter === 'not_active'}
                                    onChange={(e) => setInvoicingFilter(e.target.value)}
                                />
                                Invoicing Not Active
                            </label>
                        </div>
                    </div>
                </div>
                
                <div style={{ height: "calc(100vh - 250px)", margin: "5px" }}>
                    <ItemsTableDefault
                        tableData={getAllCampaignsTableData()}
                        clickHandler={handleTableRowClick}
                        sortColumns={sortColumns}
                        setSortColumns={setSortColumns}
                        columnsSettings={getColumnSettings()}
                        extraClass="campaigns_table"
                        noMemo={false}
                        key={tableRerenderKey} // Add key prop to trigger rerender
                    />
                </div>
            </div>
        </div>
    );
};

export default CampaignsOverview; 